import React, { useState } from 'react';
import { X, Save, MapPin } from 'lucide-react';
import Modal from '../Modal';

// Address fix from the map. For a customer entry the user can either correct the customer's
// address (changes it everywhere in the ERP) or say the machines stand elsewhere (new site).
const input = { width: '100%', padding: '10px 12px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginTop: '4px' };
const label = { display: 'block', fontSize: '13px', fontWeight: 500, color: '#333' };
const btn = (primary) => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
  cursor: 'pointer', border: primary ? 'none' : '1px solid #E0E0E0', backgroundColor: primary ? '#E53935' : '#FFF', color: primary ? '#FFF' : '#333',
});

async function api(url, method, body) {
  const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: body && JSON.stringify(body) });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.details?.[0]?.msg || json.error || `Chyba ${res.status}`);
  return json.data;
}

const Choice = ({ checked, onChange, title, text }) => (
  <label style={{ display: 'flex', gap: '10px', padding: '10px 12px', border: `1px solid ${checked ? '#E53935' : '#E0E0E0'}`, borderRadius: '8px', cursor: 'pointer', backgroundColor: checked ? '#FFEBEE' : '#FFF' }}>
    <input type="radio" checked={checked} onChange={onChange} style={{ marginTop: '3px' }} />
    <span><strong style={{ fontSize: '14px', color: '#333' }}>{title}</strong><br /><span style={{ fontSize: '12px', color: '#666' }}>{text}</span></span>
  </label>
);

const AddressModal = ({ entry, onClose, onSaved }) => {
  const isSite = entry.type === 'site';
  const [mode, setMode] = useState('address'); // 'address' | 'newSite'
  const [form, setForm] = useState({ street: entry.address.street || '', city: entry.address.city || '', postal_code: entry.address.postal_code || '', name: '' });
  const [machineIds, setMachineIds] = useState(entry.machines.map((m) => m.id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleMachine = (id) => setMachineIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const address = { street: form.street, city: form.city, postal_code: form.postal_code };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (mode === 'newSite') {
        if (machineIds.length === 0) throw new Error('Vyberte alespoň jeden trenažér, který na tomto místě stojí.');
        const site = await api(`/api/customers/${entry.id}/sites`, 'POST', { ...address, name: form.name.trim() || form.city });
        for (const mid of machineIds) await api(`/api/machines/${mid}/site`, 'PUT', { site_id: site.id });
        const geo = await api(`/api/client-map/sites/${site.id}/geocode`, 'POST');
        onSaved(`s${site.id}`, geo.status);
      } else {
        const path = isSite ? `/api/client-map/sites/${entry.site_id}` : `/api/client-map/${entry.id}`;
        const saved = await api(`${path}/address`, 'PUT', address);
        onSaved(entry.key, saved.geocode?.status ?? 'unchanged');
      }
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal show onClose={onClose}>
      <form onSubmit={submit} style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: '24px', width: 'min(520px, calc(100vw - 32px))', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}><MapPin color="#E53935" size={20} /> Upravit adresu</h2>
          <button type="button" onClick={onClose} aria-label="Zavřít" style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <div style={{ fontSize: '14px', color: '#333', fontWeight: 600 }}>{entry.name}</div>

        {!isSite && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Choice checked={mode === 'address'} onChange={() => setMode('address')} title="Opravit adresu zákazníka"
              text="Změní adresu zákazníka v celém ERP (detail zákazníka, dokumenty)." />
            <Choice checked={mode === 'newSite'} onChange={() => setMode('newSite')} title="Trenažér stojí jinde"
              text="Vytvoří novou provozovnu s touto adresou a přesune na ni vybrané trenažéry. Adresa zákazníka zůstane." />
          </div>
        )}
        {isSite && <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>Změní adresu provozovny „{entry.site_name}“. Adresa zákazníka zůstane beze změny.</p>}

        {mode === 'newSite' && (
          <label style={label}>Název provozovny
            <input style={input} value={form.name} onChange={set('name')} placeholder={form.city || 'např. Ostrava – centrum'} />
          </label>
        )}
        <label style={label}>Ulice a číslo popisné
          <input style={input} value={form.street} onChange={set('street')} placeholder="Masarykova 10" autoFocus />
        </label>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ ...label, flex: '2 1 200px' }}>Město *
            <input style={input} value={form.city} onChange={set('city')} required />
          </label>
          <label style={{ ...label, flex: '1 1 110px' }}>PSČ
            <input style={input} value={form.postal_code} onChange={set('postal_code')} placeholder="602 00" inputMode="numeric" />
          </label>
        </div>

        {mode === 'newSite' && (
          <fieldset style={{ border: '1px solid #E0E0E0', borderRadius: '8px', padding: '10px 12px', margin: 0 }}>
            <legend style={{ fontSize: '13px', fontWeight: 500, padding: '0 4px' }}>Trenažéry na tomto místě</legend>
            {entry.machines.map((m) => (
              <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', padding: '4px 0' }}>
                <input type="checkbox" checked={machineIds.includes(m.id)} onChange={() => toggleMachine(m.id)} />
                {m.model_type}{m.serial_number ? ` · SN ${m.serial_number}` : ''}
              </label>
            ))}
          </fieldset>
        )}

        {error && <p role="alert" style={{ margin: 0, color: '#D32F2F', fontSize: '13px' }}>{error}</p>}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button type="button" style={btn(false)} onClick={onClose}>Zrušit</button>
          <button type="submit" style={btn(true)} disabled={busy}><Save size={16} /> {busy ? 'Ukládám…' : 'Uložit'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default AddressModal;
